import type {
  AiOperationsCurrentProposal,
  AiOperationsRun,
  AiOperationsRunAction,
  ApprovalProposalDetail,
  Locale,
} from "@adpilot/shared";
import { runDisplayStatus } from "@/lib/ai-run-presentation";
import { formatBeijingDateTime } from "@/lib/time";

export type DecisionStatus = {
  label: string;
  tone: "success" | "warning" | "neutral" | "danger";
};

export function finalizedReviewStatus(
  run: AiOperationsRun,
  review: Pick<
    ApprovalProposalDetail,
    "actions" | "executionStatus" | "finalizationStatus" | "reviewStatus" | "routingStatus"
  >,
  actions: readonly AiOperationsRunAction[],
  proposalActionCount: number,
) {
  return runDisplayStatus(run, false, { actions, proposalActionCount, review });
}

export function canViewActionImpact(
  action: Pick<AiOperationsRunAction, "actionType" | "status"> | undefined,
): boolean {
  return (
    action?.status === "executed" &&
    (action.actionType === "increase_bid" || action.actionType === "decrease_bid")
  );
}

export function decisionRecordStatus(
  run: AiOperationsRun,
  review:
    | Pick<ApprovalProposalDetail, "actions" | "finalizationStatus" | "reviewStatus">
    | undefined,
  actionCount: number,
  pendingCount: number,
  t: (key: string, options?: Record<string, unknown>) => string,
): DecisionStatus {
  if (review?.reviewStatus === "rejected") {
    // 收尾进行中时徽章与"Finalizing review"横幅保持一致；收尾失败仍视为正常拒绝终态。
    if (review.finalizationStatus === "processing") {
      return { tone: "warning", label: t("runDetail.decisionRecord.status.finalizing") };
    }
    return { tone: "neutral", label: t("runDetail.decisionRecord.status.reviewComplete") };
  }
  if (run.status === "failed" || review?.finalizationStatus === "failed") {
    return { tone: "danger", label: t("runDetail.decisionRecord.status.needsAttention") };
  }
  if (review?.finalizationStatus === "processing") {
    return { tone: "warning", label: t("runDetail.decisionRecord.status.finalizing") };
  }
  if (review?.actions.some(({ reviewStatus }) => reviewStatus === "expired")) {
    return { tone: "warning", label: t("runDetail.decisionRecord.status.expired") };
  }
  if (review?.reviewStatus === "pending" || run.status === "suspended") {
    return {
      tone: "warning",
      label: t("runDetail.decisionRecord.status.decisionsNeeded", { count: pendingCount }),
    };
  }
  if (actionCount === 0) {
    return { tone: "success", label: t("runDetail.decisionRecord.status.noChanges") };
  }
  if (review?.reviewStatus === "approved" || review?.reviewStatus === "mixed") {
    return { tone: "success", label: t("runDetail.decisionRecord.status.reviewComplete") };
  }
  return { tone: "neutral", label: t("runDetail.decisionRecord.status.completed") };
}

export function decisionLabel(status: string, t: (key: string) => string): string {
  if (status === "approve" || status === "approved")
    return t("runDetail.decisionRecord.decision.approved");
  if (status === "reject" || status === "rejected")
    return t("runDetail.decisionRecord.decision.rejected");
  if (status === "modify_approve") return t("runDetail.decisionRecord.decision.modified");
  if (status === "not_required") return t("runDetail.decisionRecord.decision.notRequired");
  if (status === "executed" || status === "succeeded")
    return t("runDetail.decisionRecord.decision.executed");
  if (status === "failed") return t("runDetail.decisionRecord.decision.failed");
  if (status === "expired") return t("runDetail.decisionRecord.decision.expired");
  if (status === "rolled_back") return t("runDetail.decisionRecord.rollbackAction.done");
  if (status === "rolling_back") return t("runDetail.decisionRecord.rollbackAction.pending");
  if (status === "rollback_failed") return t("runDetail.decisionRecord.rollbackAction.failed");
  return status.replaceAll("_", " ");
}

export function routeLabel(route: string, t: (key: string) => string): string {
  if (route === "approval") return t("runDetail.decisionRecord.sheet.approvalRequired");
  if (route === "auto") return t("runDetail.decisionRecord.sheet.automatic");
  return t("runDetail.decisionRecord.sheet.recommendationOnly");
}

export function riskTextClass(risk: string): string {
  if (risk === "critical" || risk === "high") return "text-red-11";
  if (risk === "medium") return "text-amber-11";
  return "text-muted-foreground";
}

export function targetTypeLabel(
  action: AiOperationsCurrentProposal["artifact"]["actions"][number],
  keywordMatchType: "exact" | "phrase" | "broad" | null | undefined,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (keywordMatchType) {
    return t("runDetail.decisionRecord.target.match", {
      value: t(`runDetail.decisionRecord.target.${keywordMatchType}`),
    });
  }
  if (action.target.targetType === "product") return t("runDetail.decisionRecord.target.product");
  if (action.target.targetType === "product_category")
    return t("runDetail.decisionRecord.target.category");
  // 自动投放目标:标题已是本地化的匹配组名(targetTitle),类型行标"自动投放",
  // 不再重复匹配组——否则"Close match / 紧密匹配"两行说同一件事。
  if (action.target.expressionType) {
    return t("runDetail.decisionRecord.target.auto");
  }
  return t("runDetail.decisionRecord.target.keyword");
}

/** 行标题:自动投放目标用本地化匹配组名替换服务端的英文 label。 */
export function targetTitle(
  action: AiOperationsCurrentProposal["artifact"]["actions"][number],
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  if (action.target.expressionType) {
    return t(`runDetail.decisionRecord.target.${action.target.expressionType}`);
  }
  return action.target.label;
}

export function formatRunCompletion(
  run: AiOperationsRun,
  t: (key: string, options?: Record<string, unknown>) => string,
  locale: Locale,
): string {
  if (!run.finishedAt)
    return t("runDetail.decisionRecord.started", { date: formatRunDate(run.startedAt, locale) });
  const durationSeconds = Math.max(
    0,
    Math.round((Date.parse(run.finishedAt) - Date.parse(run.startedAt)) / 1000),
  );
  const minutes = Math.floor(durationSeconds / 60);
  const seconds = durationSeconds % 60;
  return t("runDetail.decisionRecord.completed", {
    date: formatRunDate(run.finishedAt, locale),
    duration: `${minutes}m ${seconds}s`,
  });
}

export function formatRunDate(value: string, locale: Locale): string {
  return formatBeijingDateTime(value, locale);
}
